'use client';

import { createFileRoute, Link } from '@tanstack/react-router';
import { BookOpen, LayoutGrid, Plus } from 'lucide-react';

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { useActivations } from '@/features/playbooks/api/use-playbook-activations';
import { PlaybookCard } from '@/features/playbooks/components/PlaybookCard';
import { WorkflowSectionTabs } from '@/features/playbooks/components/WorkflowSectionTabs';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/tasks/playbooks/')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.workflows'),
  }),
  component: PlaybooksIndexPage,
});

function PlaybooksIndexPage() {
  const { t } = useTranslation('tasks');
  const { activations, isLoading } = useActivations();

  return (
    <div className="space-y-6">
      <WorkflowSectionTabs activeTab="runs" />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('workflows.title')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('workflows.description')}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild data-testid="manage-templates-btn">
            <Link to="/tasks/playbooks/templates">
              <LayoutGrid className="mr-2 h-4 w-4" />
              {t('workflows.workflowTemplates')}
            </Link>
          </Button>
          <Button asChild data-testid="activate-playbook-btn">
            <Link to="/tasks/playbooks/activate">
              <Plus className="mr-2 h-4 w-4" />
              {t('workflows.newWorkflow')}
            </Link>
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-48 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      ) : activations.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16">
          <BookOpen className="mb-4 h-12 w-12 text-muted-foreground" />
          <h3 className="text-lg font-medium">{t('workflows.noWorkflowsYet')}</h3>
          <p className="mb-4 text-sm text-muted-foreground">
            {t('workflows.startNewWorkflow')}
          </p>
          <Button asChild>
            <Link to="/tasks/playbooks/activate">
              <Plus className="mr-2 h-4 w-4" />
              {t('workflows.newWorkflow')}
            </Link>
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {activations.map((activation) => (
            <PlaybookCard key={activation.id} activation={activation} />
          ))}
        </div>
      )}
    </div>
  );
}
